"use client";

// ============================================================
// CLUBHUB – /pinnwand: das Schwarze Brett
// ============================================================
// Angeheftetes oben, darunter das Neueste zuerst, als Zettel in
// Spalten (CSS-Spalten statt Tabelle — ein Brett liest man nicht
// zeilenweise). Rechts daneben, auf breiten Schirmen, was in den
// naechsten Tagen zu tun ist; auf dem Telefon traegt das Register
// „Aufgaben“ dasselbe.
// BACKEND: Filtern auf den Server, gleiche Schluessel in der Adresse.
// ============================================================
import { useState } from "react";
import Link from "next/link";
import { ArrowRight, CircleCheck, Lock, Megaphone, Plus, UsersRound } from "lucide-react";
import { Avatar, Button, Card, CardContent, CardHeader, CardTitleRow, DialogAmRumpf, EmptyState, PageHeader, toast } from "@/components/ui";
import {
  Auswahlfeld,
  Filterkopf,
  Filterloesen,
  Filterzurueck,
  Fusstext,
  Listenblatt,
  Listenfuss,
  Listenkoerper,
  Listenseite,
  Luecke,
  Schiene,
  Suchfeld,
  useAnsicht,
  Zaehlknopf,
  Zaehlschiene,
  type Auswahl,
} from "@/components/liste";
import { AUSHANG_PARAMS, ZIELE, aushangFilterZahl, type AushangAnsicht, type Ziel } from "@/lib/pinnwand/ansicht";
import { faelligDieseWoche, faelligText, istAbgelaufen, istUeberfaellig, laeuftBaldAb } from "@/lib/pinnwand/logik";
import { istFehler } from "@/lib/action-result";
import type { AushangView } from "@/lib/pinnwand/views";
import { cn } from "@/lib/utils";
import { usePinnwand } from "./pinnwand-bestand";
import { PinnwandRegister } from "./pinnwand-teile";
import { AushangKarte } from "./aushang-karte";
import { AushangDialog } from "./aushang-dialog";

type Dimension = "ziel" | "zustand";

const ZIEL_ZU_AUDIENCE: Record<Ziel, AushangView["audience"]> = { alle: "ALL", vorstand: "BOARD", gruppe: "GROUP" };

export function Aushaenge({ ansicht }: { ansicht: AushangAnsicht }) {
  const { aushaenge, gruppen, jetzt, rechte } = usePinnwand();
  const { setzen, isPending } = useAnsicht();
  const [dialog, setDialog] = useState<null | { aushang?: AushangView }>(null);

  const nadel = ansicht.q.toLowerCase();
  const passt = (a: AushangView, ausser?: Dimension) => {
    if (ausser !== "zustand" && istAbgelaufen(a, jetzt) !== ansicht.abgelaufen) return false;
    if (ausser !== "ziel") {
      if (ansicht.ziel && a.audience !== ZIEL_ZU_AUDIENCE[ansicht.ziel]) return false;
      if (ansicht.gruppe && a.groupId !== ansicht.gruppe) return false;
    }
    if (nadel) {
      const gruppe = gruppen.find((g) => g.id === a.groupId)?.name;
      if (![a.title, a.body, a.authorName, gruppe].filter(Boolean).join(" ").toLowerCase().includes(nadel)) return false;
    }
    return true;
  };

  const gezeigt = aushaenge
    .filter((a) => passt(a))
    .sort((x, y) => y.createdAt.localeCompare(x.createdAt));
  const angeheftet = ansicht.abgelaufen ? [] : gezeigt.filter((a) => a.isPinned);
  const weitere = ansicht.abgelaufen ? gezeigt : gezeigt.filter((a) => !a.isPinned);

  const fuerZiel = aushaenge.filter((a) => passt(a, "ziel"));
  const zielZaehler = Object.fromEntries(ZIELE.map((z) => [z.schluessel, fuerZiel.filter((a) => a.audience === z.audience).length])) as Record<Ziel, number>;
  const gruppenWahl: Auswahl[] = gruppen
    .map((g) => ({ id: g.id, name: g.name, anzahl: fuerZiel.filter((a) => a.groupId === g.id).length }))
    .filter((g) => g.anzahl > 0 || g.id === ansicht.gruppe);
  const fuerZustand = aushaenge.filter((a) => passt(a, "zustand"));
  const zustandZaehler = {
    aktuell: fuerZustand.filter((a) => !istAbgelaufen(a, jetzt)).length,
    abgelaufen: fuerZustand.filter((a) => istAbgelaufen(a, jetzt)).length,
  };
  const aktuelle = aushaenge.filter((a) => !istAbgelaufen(a, jetzt));
  const baldAb = gezeigt.filter((a) => laeuftBaldAb(a, jetzt)).length;
  const filterZahl = aushangFilterZahl(ansicht);

  const neu = rechte.schreiben ? (
    <Button onClick={() => setDialog({})}>
      <Plus className="h-4 w-4" aria-hidden />
      Neuer Aushang
    </Button>
  ) : undefined;

  return (
    <Listenseite>
      <PageHeader
        title="Pinnwand"
        description={`${aktuelle.length} aktuelle Aushänge · ${aktuelle.filter((a) => a.isPinned).length} angeheftet`}
        actions={neu}
      />
      <PinnwandRegister />

      <Listenblatt>
        <Filterkopf
          isPending={isPending}
          oben={
            <>
              <Suchfeld wert={ansicht.q} platzhalter="Überschrift, Text, Name …" beschriftung="Aushänge durchsuchen" />
              <Luecke />
              <Zaehlschiene titel="Für">
                {ZIELE.map((z) => (
                  <Zaehlknopf
                    key={z.schluessel}
                    label={z.label}
                    anzahl={zielZaehler[z.schluessel]}
                    aktiv={ansicht.ziel === z.schluessel}
                    onClick={() => setzen({ ziel: ansicht.ziel === z.schluessel ? null : z.schluessel, gruppe: null })}
                  />
                ))}
              </Zaehlschiene>
              <Schiene>
                <Zaehlknopf label="Aktuell" anzahl={zustandZaehler.aktuell} aktiv={!ansicht.abgelaufen} onClick={() => setzen({ zustand: null })} />
                <Zaehlknopf
                  label="Abgelaufen"
                  anzahl={zustandZaehler.abgelaufen}
                  aktiv={ansicht.abgelaufen}
                  titel="Nach dem Ablaufdatum — für niemanden mehr sichtbar, hier zum Nachlesen"
                  onClick={() => setzen({ zustand: ansicht.abgelaufen ? null : "abgelaufen" })}
                />
              </Schiene>
            </>
          }
          unten={
            <>
              <Schiene>
                <Auswahlfeld
                  icon={UsersRound}
                  titel="Gruppe"
                  label="Gruppe"
                  eintraege={gruppenWahl}
                  gewaehlt={ansicht.gruppe}
                  onWaehlen={(id) => setzen({ gruppe: id, ziel: id ? "gruppe" : null })}
                  suchtext="Gruppe suchen …"
                  alleLabel="Alle Gruppen"
                  leerText="Keine Gruppe mit Aushängen."
                />
              </Schiene>
              <Filterzurueck anzahl={filterZahl} params={AUSHANG_PARAMS} />
            </>
          }
        />

        <Listenkoerper className="p-4">
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_20rem]">
            <div className="min-w-0">
              {gezeigt.length === 0 ? (
                filterZahl > 0 && !(ansicht.abgelaufen && filterZahl === 1) ? (
                  <EmptyState
                    icon={Megaphone}
                    title="Kein Aushang passt zu diesem Filter"
                    description="Ändere die Suche oder setze die Filter zurück."
                    action={<Filterloesen params={AUSHANG_PARAMS} />}
                  />
                ) : ansicht.abgelaufen ? (
                  <EmptyState icon={Megaphone} title="Nichts abgelaufen" description="Aushänge mit Ablaufdatum landen danach hier — zum Nachlesen." />
                ) : (
                  <EmptyState
                    icon={Megaphone}
                    title="Die Pinnwand ist leer"
                    description="Hänge den ersten Aushang auf: Trainingszeiten, Einladungen, Helfer gesucht. Mitglieder sehen ihn im Mitgliederbereich."
                    action={neu}
                  />
                )
              ) : (
                <>
                  {angeheftet.length > 0 && (
                    <section aria-label="Angeheftet" className="mb-2">
                      <h2 className="text-overline text-text-tertiary mb-2 px-1">Angeheftet</h2>
                      <div className="columns-1 gap-4 md:columns-2 2xl:columns-3">
                        {angeheftet.map((a) => (
                          <AushangKarte key={a.id} aushang={a} onBearbeiten={() => setDialog({ aushang: a })} />
                        ))}
                      </div>
                    </section>
                  )}
                  {weitere.length > 0 && (
                    <section aria-label={ansicht.abgelaufen ? "Abgelaufen" : "Neueste zuerst"}>
                      {angeheftet.length > 0 && <h2 className="text-overline text-text-tertiary mb-2 px-1">Neueste zuerst</h2>}
                      <div className="columns-1 gap-4 md:columns-2 2xl:columns-3">
                        {weitere.map((a) => (
                          <AushangKarte key={a.id} aushang={a} onBearbeiten={() => setDialog({ aushang: a })} />
                        ))}
                      </div>
                    </section>
                  )}
                </>
              )}
            </div>
            <BaldFaellig />
          </div>
        </Listenkoerper>

        <Listenfuss>
          <Fusstext titel="Summe" zusatz={`${gezeigt.length} ${gezeigt.length === 1 ? "Aushang" : "Aushänge"}`} />
          {baldAb > 0 && (
            <span className="text-body-sm text-text-secondary">
              Laufen in 7 Tagen ab <span className="numeric text-warning font-medium">{baldAb}</span>
            </span>
          )}
          <span className="text-caption text-text-tertiary inline-flex items-center gap-1.5 md:ml-auto">
            <Lock className="h-3 w-3" aria-hidden />
            Nur für angemeldete Mitglieder sichtbar
          </span>
        </Listenfuss>
      </Listenblatt>

      <DialogAmRumpf>
        <AushangDialog offen={dialog !== null} onClose={() => setDialog(null)} aushang={dialog?.aushang} />
      </DialogAmRumpf>
    </Listenseite>
  );
}

/** Rechts neben dem Brett: was ueberfaellig ist oder in den naechsten sieben Tagen faellig. */
function BaldFaellig() {
  const { aufgaben, personen, heute, rechte, aufgabeStatus } = usePinnwand();
  const liste = aufgaben
    .filter((t) => istUeberfaellig(t, heute) || faelligDieseWoche(t, heute))
    .sort((a, b) => (a.dueDate ?? "").localeCompare(b.dueDate ?? ""))
    .slice(0, 6);

  return (
    <Card className="hidden self-start xl:block">
      <CardHeader>
        <CardTitleRow
          title="Bald fällig"
          action={
            <Link href="/pinnwand/aufgaben" className="text-body-sm text-text-secondary hover:text-text-primary flex items-center gap-1">
              Aufgaben <ArrowRight className="h-3.5 w-3.5" aria-hidden />
            </Link>
          }
        />
      </CardHeader>
      <CardContent className="space-y-1">
        {liste.length === 0 && <p className="text-body-sm text-text-tertiary">Nichts fällig in den nächsten sieben Tagen.</p>}
        {liste.map((t) => {
          const p = t.assigneeId ? personen[t.assigneeId] : undefined;
          const ueber = istUeberfaellig(t, heute);
          return (
            <div key={t.id} className="hover:bg-bg-secondary duration-fast -mx-2 flex items-start gap-2.5 rounded-lg px-2 py-2 transition-colors">
              {rechte.schreiben && (
                <button
                  type="button"
                  onClick={async () => {
                    const r = await aufgabeStatus(t.id, "DONE");
                    if (istFehler(r)) {
                      toast.error(r.error);
                      return;
                    }
                    toast.success(`Erledigt: ${t.title}`, {
                      action: {
                        label: "Rückgängig",
                        onClick: () =>
                          void aufgabeStatus(t.id, t.status).then((z) => {
                            if (istFehler(z)) toast.error(z.error);
                          }),
                      },
                    });
                  }}
                  title="Als erledigt markieren"
                  aria-label={`„${t.title}“ als erledigt markieren`}
                  className="text-text-tertiary hover:text-success mt-px shrink-0"
                >
                  <CircleCheck className="h-4 w-4" aria-hidden />
                </button>
              )}
              <Link href="/pinnwand/aufgaben" className="min-w-0 flex-1">
                <p className="text-body-sm text-text-primary leading-snug">{t.title}</p>
                <p className={cn("text-caption numeric", ueber ? "text-danger font-medium" : "text-text-tertiary")}>{faelligText(t, heute)}</p>
              </Link>
              {p && <Avatar firstName={p.firstName} lastName={p.lastName} size="sm" className="h-6 w-6 text-[10px]" hint={p.displayName} />}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
